// src/oura/overview/AttentionCard.tsx
//
// "Needs attention": problems first, then what is not set up, then the dates
// coming up. Every row links to the screen where it is dealt with, and a row's
// explanation wraps rather than truncates: it is the part worth reading.

import { FlagIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { Dot, FoldCard, ListRow, TYPE } from "@oura/ui";
import { count, staffHref, type AttentionRow } from "./model";

export function AttentionCard({
    businessId,
    rows,
    loading,
    unread,
}: {
    businessId: string;
    rows: AttentionRow[];
    /** A source is still on its way, so the list may grow. */
    loading: boolean;
    /** The sources that failed to load, named (`couldNotRead`), so the list may be short. Said, never hidden. */
    unread: string | null;
}) {
    return (
        <FoldCard
            id="attention"
            title="Needs attention"
            icon={{ icon: FlagIcon, area: "asks" }}
            right={loading ? undefined : <span className="tabular-nums">{count(rows.length, "item")}</span>}
        >
            {rows.length > 0 ? (
                <div>
                    {rows.map((r) => (
                        <ListRow
                            key={r.key}
                            icon={<Dot tone={r.tone} />}
                            title={<span className="whitespace-normal font-medium">{r.title}</span>}
                            sub={r.sub}
                            to={staffHref(businessId, r.link)}
                            className="py-2.5"
                        />
                    ))}
                </div>
            ) : (
                !loading && <p className={cn(TYPE.tc, "m-0 py-2 text-gray-500")}>Nothing needs attention.</p>
            )}
            {loading && (
                <div aria-hidden className="flex min-h-12 items-center gap-3 border-t border-gray-100 first:border-t-0">
                    <span className="h-2 w-2 rounded-full bg-gray-200" />
                    <span className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
                </div>
            )}
            {unread && <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>{unread}, so this list may be short.</p>}
        </FoldCard>
    );
}
